import {Component, computed, Inject, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {DateTime} from 'luxon';
import {DialogBaseComponent} from '../dialog-base/dialog-base.component';
import {GeographyService} from '../../../services/geography.service';
import {ErrorHandlingService} from '../../../services/error-handling.service';
import {SnackbarService} from '../../../services/snackbar.service';
import {IRegionTaxRate} from '../../../interfaces/IRegionTaxRate';
import {IUpdateRegionTaxRate} from '../../../interfaces/IUpdateRegionTaxRate';
import {RegionTypes} from '../../../enum/RegionTypes';
import {UIStateEnum} from '../../../enum/UIStateEnum';

export interface IEditRegionTaxRateDialogData {
  currentRate: IRegionTaxRate;
  regionId: string;
  regionType: RegionTypes;
}

@Component({
  selector: 'app-edit-region-tax-rate-dialog',
  standalone: true,
  imports: [
    DialogBaseComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInput,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatIcon
  ],
  templateUrl: './edit-region-tax-rate-dialog.component.html',
  styleUrl: './edit-region-tax-rate-dialog.component.css'
})
export class EditRegionTaxRateDialogComponent {

  public uiState = UIStateEnum.ShowData;
  public uiStateEnum = UIStateEnum;
  public minEffectiveDate = DateTime.now();
  public isConfirming = signal(false);

  public pstLabel = computed(() => this.dialogData.regionType === RegionTypes.Quebec ? 'QST' : 'PST');
  public pstHint = computed(() => this.dialogData.regionType === RegionTypes.Quebec ? 'Quebec Sales Tax' : 'Provincial Sales Tax');

  public taxRateForm = new FormGroup({
    gstRate: new FormControl<number>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
    pstRate: new FormControl<number>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
    hstRate: new FormControl<number>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
    effectiveDate: new FormControl<DateTime | null>(DateTime.now().plus({days: 1}), [Validators.required])
  });

  public formatRate(decimalRate: number): string {
    const pct = Math.round(decimalRate * 100000) / 1000;
    return pct === 0 ? '0%' : `${pct}%`;
  }

  public get isUnchanged(): boolean {
    const rate = this.dialogData.currentRate;
    const toPercent = (decimal: number) => Math.round(decimal * 100000) / 1000;
    return this.taxRateForm.controls.gstRate.value === toPercent(rate.GstRate)
      && this.taxRateForm.controls.pstRate.value === toPercent(rate.PstRate)
      && this.taxRateForm.controls.hstRate.value === toPercent(rate.HstRate);
  }

  public get isEffectiveDateToday(): boolean {
    const date = this.taxRateForm.controls.effectiveDate.value;
    return date ? date.hasSame(DateTime.now(), 'day') : false;
  }

  public get saveButtonLabel(): string {
    const date = this.taxRateForm.controls.effectiveDate.value;
    if (!date) return 'Save Tax Rates';
    if (date.hasSame(DateTime.now(), 'day')) return 'Apply Now';
    return `Schedule for ${date.toFormat('MMM d, yyyy')}`;
  }

  constructor(
    private geographyService: GeographyService,
    private errorHandlingService: ErrorHandlingService,
    private snackbarService: SnackbarService,
    private matDialogRef: MatDialogRef<EditRegionTaxRateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: IEditRegionTaxRateDialogData
  ) {
    const rate = dialogData.currentRate;
    this.taxRateForm.controls.gstRate.setValue(Math.round(rate.GstRate * 100000) / 1000);
    this.taxRateForm.controls.pstRate.setValue(Math.round(rate.PstRate * 100000) / 1000);
    this.taxRateForm.controls.hstRate.setValue(Math.round(rate.HstRate * 100000) / 1000);
  }

  public save(): void {
    if (!this.taxRateForm.valid) {
      this.taxRateForm.markAllAsTouched();
      return;
    }
    this.isConfirming.set(true);
  }

  public backToEdit(): void {
    this.isConfirming.set(false);
  }

  public confirm(): void {
    const effectiveDateValue = this.taxRateForm.controls.effectiveDate.value!;
    const isImmediate = effectiveDateValue.hasSame(DateTime.now(), 'day');

    const command: IUpdateRegionTaxRate = {
      RegionId: this.dialogData.regionId,
      GstRate: (this.taxRateForm.controls.gstRate.value || 0) / 100,
      PstRate: (this.taxRateForm.controls.pstRate.value || 0) / 100,
      HstRate: (this.taxRateForm.controls.hstRate.value || 0) / 100,
      StartDate: effectiveDateValue.toISODate()!
    };

    this.uiState = UIStateEnum.ShowLoading;

    this.geographyService.updateRegionTaxRate(command).subscribe({
      next: () => {
        const successMessage = isImmediate
          ? 'Tax rates updated successfully'
          : `Tax rate change scheduled for ${effectiveDateValue.toFormat('MMMM d, yyyy')}`;
        this.snackbarService.openSuccessfulSnackBar(successMessage);
        this.matDialogRef.close(true);
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.isConfirming.set(false);
        this.errorHandlingService.displayDialogLevelErrorMessage(err, true);
      }
    });
  }
}
